import React, { memo, useState } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import CreateIcon from "@mui/icons-material/Create";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import { palette } from "../../theme/palette";
import {
  DRIVER_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING,
  SELECTING_DRIVER_LOCATION_ACTIVE_WARNING,
} from "../../consts/strings";

import { addNotification } from "../../reducers/ownApp";
import { onDriverClick } from "../../reducers/ownApp/thunkActions";
import { editDriver } from "../../reducers/driverManagement/thunkActions";

import { BinIcon } from "../Icons";
import { View } from "../View";
import { RemoveDriverModal } from "../Modals/RemoveDriver/RemoveDriverModal";
import { DriverLabel } from "./DriverLabel";
import DriverName from "./DriverName";
import { DriverCardScheduleContent } from "./DriverCardScheduleContent";
import { DriverCardProfileContent } from "./DriverCardProfileContent";
import { CopyDriverWorkScheduleModal } from "../Modals/CopyDriverWorkSchedule/CopyDriverWorkScheduleModal";

const useStyles = makeStyles(() => {
  return {
    section: {
      display: "flex",
      flexDirection: "column",
      background: palette.typography.white.neutral,
      padding: "0.625rem",
      paddingBottom: "0.5rem",
      cursor: "default",
      height: (props) => (props.height || 1) - 1,
    },
    highlighted: {
      background: palette.typography.white.disabled,
    },
    editIconBase: {
      backgroundColor: (props) =>
        props.editing
          ? palette.orange
          : props.notInWave
          ? "#30303044"
          : "#303030",
      borderRadius: "50%",
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    editIcon: {
      color: palette.typography.white.solid,
      width: "13px",
      height: "13px",
      cursor: "pointer",
    },
    copyIcon: {
      marginTop: "10px",
      fill: palette.typography.white.medium,
      height: "20px",
      width: "20px",
      cursor: "pointer",
    },
    binIcon: {
      marginTop: "10px",
      fill: palette.typography.white.solid,
      height: "20px",
      width: "20px",
      cursor: "pointer",
    },
    top: {
      display: "flex",
      flexDirection: "row",
      gap: "0.3rem",
    },
    bottom: {
      display: "flex",
      flexDirection: "row",
      gap: "0.3rem",
    },
    leftButtons: {
      width: "2.5rem",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    right: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginLeft: "0.5rem",
      color: "white",
      lineHeight: "1.25rem",
      gap: "0.25rem",
    },
  };
});

export const DriverCardBase = ({
  showWorkSchedule,
  height,
  driver,
  vehicle,
  driverProfileLocks,
  matrixDays,
  editMode,
  selectingDriverLocation,
  inEdit,
  selected,
}) => {
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const dispatch = useDispatch();
  const styles = useStyles({ editing: inEdit, height });

  const handleClick = (e) => {
    e.preventDefault();
    if (editMode)
      dispatch(
        addNotification({
          message: DRIVER_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(driver),
          options: { variant: "info" },
        })
      );
    else if (selectingDriverLocation)
      dispatch(
        addNotification({
          message: SELECTING_DRIVER_LOCATION_ACTIVE_WARNING,
          options: { variant: "info" },
        })
      );
    else dispatch(onDriverClick(driver));
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (editMode && !inEdit)
      dispatch(
        addNotification({
          message: DRIVER_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(driver),
          options: { variant: "info" },
        })
      );
    else if (selectingDriverLocation)
      dispatch(
        addNotification({
          message: SELECTING_DRIVER_LOCATION_ACTIVE_WARNING,
          options: { variant: "info" },
        })
      );
    else if (!editMode) dispatch(editDriver(driver));
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    if (editMode && !inEdit)
      dispatch(
        addNotification({
          message: DRIVER_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(driver),
          options: { variant: "info" },
        })
      );
    else if (selectingDriverLocation)
      dispatch(
        addNotification({
          message: SELECTING_DRIVER_LOCATION_ACTIVE_WARNING,
          options: { variant: "info" },
        })
      );
    else setShowCopyModal(true);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setShowRemoveModal(true);
  };

  return (
    <div style={{ height }}>
      <View
        className={clsx(
          styles.section,
          (inEdit || selected) && styles.highlighted
        )}
        onClick={handleClick}
      >
        <View className={styles.top}>
          <DriverLabel driver={driver} />
          <DriverName driver={driver} />
        </View>
        <View className={styles.bottom}>
          <View className={styles.leftButtons}>
            <View className={styles.editIconBase} onClick={handleEdit}>
              <CreateIcon fontSize={"small"} className={styles.editIcon} />
            </View>
            {showWorkSchedule && (
              <FileCopyIcon className={styles.copyIcon} onClick={handleCopy} />
            )}
            <BinIcon className={styles.binIcon} onClick={handleClear} />
          </View>

          {!showWorkSchedule ? (
            <DriverCardProfileContent driver={driver} vehicle={vehicle} />
          ) : (
            <DriverCardScheduleContent
              driver={driver}
              driverProfileLocks={driverProfileLocks}
              matrixDays={matrixDays}
            />
          )}
        </View>
      </View>
      {showCopyModal && (
        <CopyDriverWorkScheduleModal
          driver={driver}
          onClose={() => setShowCopyModal(false)}
        />
      )}
      {showRemoveModal && (
        <RemoveDriverModal
          driver={driver}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
    </div>
  );
};

export default memo(DriverCardBase);
